import React from 'react';
import { Auth0Provider, useAuth0 } from '@auth0/auth0-react';
import { createBrowserRouter } from 'react-router';
import { RouterProvider } from 'react-router/dom';
import routes from './AppRoutes';
import dagLogoDark from './assets/dag.svg';
import dagLogoLight from './assets/dag-light.svg';
import { APP_BASE_PATH } from './constants/appBasePath';
import { ThemeProvider, useTheme } from './contexts/ThemeContext';
import { LoginProfileProvider } from './contexts/LoginProfileContext';

const requiredEnvVars = [
  'REACT_APP_AUTH0_DOMAIN',
  'REACT_APP_AUTH0_UI_CLIENTID',
  'REACT_APP_AUTH0_API_AUDIENCE',
  // The dashboard is a frontend-only aggregator: one base URL per backing service.
  'REACT_APP_LLC_API_URL',
  'REACT_APP_LLC_URL',
  'REACT_APP_CHECKOUT_API_URL',
];

const missingEnvVars = requiredEnvVars.filter((v) => !process.env[v]);
if (missingEnvVars.length > 0) {
  throw new Error(
    `Missing required environment variables: ${missingEnvVars.join(', ')}. Check your .env.local file.`
  );
}

const auth0Config = {
  domain: process.env.REACT_APP_AUTH0_DOMAIN!,
  clientId: process.env.REACT_APP_AUTH0_UI_CLIENTID!,
  authorizationParams: {
    // Intentionally raw process.env.PUBLIC_URL, not APP_BASE_PATH — must match what's registered
    // in Auth0 Allowed Callback URLs. PUBLIC_URL = '/dashboard' in prod builds, '' in dev.
    redirect_uri: `${window.location.origin}${process.env.PUBLIC_URL || ''}`,
    audience: process.env.REACT_APP_AUTH0_API_AUDIENCE!,
    scope: 'openid profile email',
  },
  useRefreshTokens: true,
  useRefreshTokensFallback: true,
  cacheLocation:
    process.env.NODE_ENV === 'development'
      ? ('localstorage' as const)
      : ('memory' as const),
  onRedirectCallback: (appState: any) => {
    const returnTo = appState?.returnTo;
    const appRoot = window.location.origin + APP_BASE_PATH;

    if (!returnTo || returnTo === '/') {
      window.location.href = appRoot;
      return;
    }

    const url = new URL(returnTo, window.location.origin);
    window.location.href =
      url.origin === window.location.origin ? url.href : appRoot;
  },
};

const router = createBrowserRouter(routes, {
  basename: APP_BASE_PATH || '/',
});

const Auth0ErrorHandler: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { error, loginWithRedirect } = useAuth0();
  const { resolvedTheme } = useTheme();
  const dagLogo = resolvedTheme === 'dark' ? dagLogoDark : dagLogoLight;

  if (error) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-surface-secondary p-8">
        <div className="w-full max-w-md rounded-[10px] border border-theme-border bg-surface px-10 py-8 text-center">
          <img src={dagLogo} alt="DAG" className="mx-auto mb-6 h-10 w-auto" />
          <h1 className="mb-2 text-2xl font-semibold text-on-surface">
            Login Failed
          </h1>
          <p className="mb-6 text-base text-on-surface-tertiary">
            {error.message}
          </p>
          <button
            onClick={() => loginWithRedirect()}
            className="w-full rounded-none bg-gold-500 px-6 py-3.5 text-base font-semibold text-ink transition-colors hover:bg-gold-600"
          >
            Back to Login
          </button>
        </div>
      </div>
    );
  }

  return <>{children}</>;
};

const AppContent: React.FC = () => {
  const { isLoading } = useAuth0();

  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-surface-secondary">
        <p className="text-sm text-on-surface-tertiary">Loading...</p>
      </div>
    );
  }

  return (
    <Auth0ErrorHandler>
      <LoginProfileProvider>
        <RouterProvider router={router} />
      </LoginProfileProvider>
    </Auth0ErrorHandler>
  );
};

const App: React.FC = () => {
  return (
    <ThemeProvider>
      <Auth0Provider {...auth0Config}>
        <AppContent />
      </Auth0Provider>
    </ThemeProvider>
  );
};

export default App;
